import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { getSite, listForms, type Site, type FormRow } from '../lib/db';
import { LoadingIndicator } from '../components/LoadingIndicator';

/**
 * Forms history — flat list of every form recorded on a site.
 *
 * Lives on its own page (route: /sites/:siteId/forms/history) so the action
 * menu at /sites/:siteId/forms can stay clean (just the three "Start a new
 * form" tiles). Reached via the navy banner at the bottom of the action menu.
 */

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

const KIND_LABEL: Record<FormRow['kind'], string> = {
  toolbox_talk: 'Toolbox talk',
  accident: 'Accident / near-miss',
  subcontractor_induction: 'Subbie induction',
  other: 'Form',
};

export function FormsHistory() {
  const { siteId } = useParams<{ siteId: string }>();
  const [site, setSite] = useState<Site | null>(null);
  const [forms, setForms] = useState<FormRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kindFilter, setKindFilter] = useState<FormRow['kind'] | 'all'>('all');

  useEffect(() => {
    if (!siteId) return;
    Promise.all([getSite(siteId), listForms(siteId, undefined, 500)])
      .then(([s, f]) => { setSite(s); setForms(f); })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [siteId]);

  if (loading) {
    return <AppShell title="Forms on this site" back={`/sites/${siteId ?? ''}/forms`}><LoadingIndicator /></AppShell>;
  }
  if (!site) {
    return <AppShell title="Forms on this site" back={`/sites/${siteId ?? ''}/forms`}><div className="banner banner-error">{error ?? 'Site not found.'}</div></AppShell>;
  }

  const filtered = kindFilter === 'all' ? forms : forms.filter((f) => f.kind === kindFilter);

  const counts = {
    toolbox_talk: forms.filter((f) => f.kind === 'toolbox_talk').length,
    accident: forms.filter((f) => f.kind === 'accident').length,
    subcontractor_induction: forms.filter((f) => f.kind === 'subcontractor_induction').length,
  };

  return (
    <AppShell title="Forms on this site" back={`/sites/${site.id}/forms`}>
      {error && <div className="banner banner-error">{error}</div>}

      {forms.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', marginTop: 12 }}>
          <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
            No forms saved yet for <strong>{site.name}</strong>. Tap "‹ Back" to record one.
          </p>
        </div>
      ) : (
        <>
          <div className="card" style={{ marginBottom: 12 }}>
            <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
              <strong>{forms.length}</strong> recorded on <strong>{site.name}</strong> · tap a row to view, edit follow-ups or export the PDF.
            </p>
          </div>

          {/* Filter chips */}
          <div className="rams-trade-chips" role="tablist" aria-label="Filter by form type" style={{ marginBottom: 12 }}>
            <button
              type="button"
              className={`rams-trade-chip ${kindFilter === 'all' ? 'active' : ''}`}
              onClick={() => setKindFilter('all')}
            >
              All ({forms.length})
            </button>
            <button
              type="button"
              className={`rams-trade-chip ${kindFilter === 'accident' ? 'active' : ''}`}
              onClick={() => setKindFilter('accident')}
            >
              Accidents ({counts.accident})
            </button>
            <button
              type="button"
              className={`rams-trade-chip ${kindFilter === 'toolbox_talk' ? 'active' : ''}`}
              onClick={() => setKindFilter('toolbox_talk')}
            >
              Toolbox talks ({counts.toolbox_talk})
            </button>
            <button
              type="button"
              className={`rams-trade-chip ${kindFilter === 'subcontractor_induction' ? 'active' : ''}`}
              onClick={() => setKindFilter('subcontractor_induction')}
            >
              Subbie inductions ({counts.subcontractor_induction})
            </button>
          </div>

          {filtered.length === 0 ? (
            <div className="card" style={{ textAlign: 'center' }}>
              <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
                Nothing in this category yet — try "All" above.
              </p>
            </div>
          ) : (
            <ul className="person-list">
              {filtered.map((f) => (
                <li key={f.id}>
                  <Link to={`/forms/${f.id}`} className="site-item-link">
                    <div>
                      <p className="person-name">
                        {f.title ?? KIND_LABEL[f.kind]}
                        {f.is_riddor && (
                          <span className="status-pill status-danger" style={{ marginLeft: 8 }}>RIDDOR</span>
                        )}
                      </p>
                      <p className="person-meta">
                        {KIND_LABEL[f.kind]} · {f.performed_by_name ?? 'Unknown'} · {fmtDateTime(f.performed_at)}
                      </p>
                    </div>
                    <span className="chev">›</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <div style={{ marginTop: 24, display: 'flex', gap: 10 }}>
        <Link to={`/sites/${site.id}/forms`} className="btn btn-secondary" style={{ flex: 1 }}>
          ‹ Back to forms menu
        </Link>
      </div>
    </AppShell>
  );
}
